import { Flavor } from '@ag1/ui';
import {
  FormattedShopifyProduct,
  ShopifyProductFlavorsMap
} from '@portal/types/shopify/product.formatted.types';
import { SHOPIFY_PRODUCT_TAGS_OF_INTEREST } from '@portal/constants/shop-v3';

import {
  getFlavorColorMetafield,
  getFlavorGroupNameMetafield,
  getFlavorNameMetafield,
  getIsMemberOnlyMetafield
} from './metafields';

/* ------ flavors related ------- */

// determines if a product has the right metafield setup to be considered a flavor product
export const isFlavorProduct = (product: FormattedShopifyProduct) => {
  return (
    getFlavorGroupNameMetafield(product)?.value &&
    getFlavorNameMetafield(product)?.value &&
    getFlavorColorMetafield(product)?.value
  );
};

// determines if the targeted product has a "title" value not in current product list
// if yes, in order for it to count, it must also share the same formula tag (V52, V53)
export const hasUniqueProductName = (
  productList: FormattedShopifyProduct[],
  targetProduct: FormattedShopifyProduct
) => {
  return !productList.some((existingProduct) => {
    const isSameTitle = existingProduct.title === targetProduct.title;
    return existingProduct.title.includes('AG1')
      ? isSameTitle &&
          existingProduct.tags.some(
            (tag) =>
              SHOPIFY_PRODUCT_TAGS_OF_INTEREST.includes(tag) && targetProduct.tags.includes(tag)
          )
      : isSameTitle;
  });
};

// determines if the targeted product has a flavor group name value not in current product list
export const hasUniqueFlavorGroupName = (
  productsList: FormattedShopifyProduct[],
  targetProduct: FormattedShopifyProduct
) => {
  const targetFlavorGroupName = getFlavorGroupNameMetafield(targetProduct)?.value;
  return !productsList.some((existingProduct) =>
    existingProduct.metafields.some(
      (metafield) =>
        metafield && metafield.key === 'flavor_group' && metafield.value === targetFlavorGroupName
    )
  );
};

// using an existing product list, generate a map of flavor group name to a list of shopify products
// in that flavor group
export const consolidateFlavorProducts = (
  productsList: FormattedShopifyProduct[] | undefined,
  isLoggedIn: boolean
) => {
  const flavorsMap = {} as ShopifyProductFlavorsMap;

  if (!productsList) return flavorsMap;

  productsList.forEach((productData) => {
    const currentFlavorGroupName = getFlavorGroupNameMetafield(productData)?.value;
    const isMembersOnlyProduct = getIsMemberOnlyMetafield(productData)?.value === 'true';

    // only add a product to the map if:
    // - the product has the proper flavor metafields and,
    //   - either it's not a members-only product or,
    //   - user is logged in and it's a members-only product
    if (isFlavorProduct(productData) && currentFlavorGroupName) {
      if (!isMembersOnlyProduct || (isMembersOnlyProduct && isLoggedIn)) {
        if (Object.prototype.hasOwnProperty.call(flavorsMap, currentFlavorGroupName)) {
          flavorsMap[currentFlavorGroupName]?.push(productData);
        } else {
          flavorsMap[currentFlavorGroupName] = [productData];
        }
      }
    }
  });

  // remove a flavor group if the list of products is empty
  Object.keys(flavorsMap).forEach((flavorGroupName) => {
    if (!flavorsMap[flavorGroupName].length) {
      delete flavorsMap[flavorGroupName];
    }
  });

  return flavorsMap;
};

export const removeDuplicateProducts = (productsList: FormattedShopifyProduct[] | undefined) => {
  const filteredProducts = [] as FormattedShopifyProduct[];

  if (!productsList) return filteredProducts;

  productsList.forEach((currentProductData) => {
    const isProductWitUniqueName = hasUniqueProductName(filteredProducts, currentProductData);
    const isFlavorVariantProduct = isFlavorProduct(currentProductData);
    const isProductWithUniqueFlavorGroupName = hasUniqueFlavorGroupName(
      filteredProducts,
      currentProductData
    );

    if (
      (!isFlavorVariantProduct && isProductWitUniqueName) ||
      (isFlavorVariantProduct && isProductWithUniqueFlavorGroupName)
    ) {
      filteredProducts.push(currentProductData);
    }
  });

  return filteredProducts;
};

// convert an array of grouped-by-flavor shopify products into renderable
// flavor data the ui library can use
export const formatToRenderableFlavorData = (flavorsList: FormattedShopifyProduct[]) => {
  return flavorsList?.map((productData) => ({
    id: productData.id,
    backgroundColor: getFlavorColorMetafield(productData)?.value as string,
    label: getFlavorNameMetafield(productData)?.value as string

    // (optional)
    // define a color map for your flavors text if you do decide to render it into
    // the flavor bubble, as there is no way for the UI package to determine whether
    // the flavor text should be black or white
    // useWhiteText: FLAVOR_TEXT_COLOR_MAP[flavorLabel] && true
  })) as [Flavor, ...Flavor[]];
};
